/**
 * useExternalUsers Hook
 * Manages external users state for admin UI
 */
import { useState, useEffect, useCallback } from 'react';
import { externalUsersApi } from '../api/externalUsersApi';
import {
    ExternalUser,
    CreateExternalUserPayload,
    UpdateExternalUserPayload,
} from '../types/guestAccess';

interface UseExternalUsersReturn {
    users: ExternalUser[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    refetch: () => Promise<void>;
    createUser: (payload: CreateExternalUserPayload) => Promise<string>;
    updateUser: (payload: UpdateExternalUserPayload) => Promise<void>;
    deleteUser: (uid: string, replacementName?: string) => Promise<void>;
    clearError: () => void;
}

interface UseExternalUsersOptions {
    /**
     * When false, the hook does not fetch on mount (loading stays false).
     * Use this when the external users are supplied from elsewhere and this
     * hook is only mounted to satisfy the rules of hooks.
     */
    enabled?: boolean;
}

export const useExternalUsers = (options?: UseExternalUsersOptions): UseExternalUsersReturn => {
    const enabled = options?.enabled ?? true;
    const [users, setUsers] = useState<ExternalUser[]>([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const fetchedUsers = await externalUsersApi.list();
            setUsers(fetchedUsers);
        } catch (err) {
            console.error('Error fetching external users:', err);
            setError('Kunde inte ladda externa användare');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (enabled) fetchUsers();
    }, [enabled, fetchUsers]);

    const createUser = useCallback(async (payload: CreateExternalUserPayload): Promise<string> => {
        setSubmitting(true);
        setError(null);

        try {
            const uid = await externalUsersApi.create(payload);
            await fetchUsers();
            return uid;
        } catch (err) {
            console.error('Error creating external user:', err);
            const message = err instanceof Error ? err.message : 'Kunde inte skapa användare';
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [fetchUsers]);

    const updateUser = useCallback(async (payload: UpdateExternalUserPayload): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await externalUsersApi.update(payload);
            await fetchUsers();
        } catch (err) {
            console.error('Error updating external user:', err);
            const message = err instanceof Error ? err.message : 'Kunde inte uppdatera användare';
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [fetchUsers]);

    const deleteUser = useCallback(async (uid: string, replacementName?: string): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await externalUsersApi.delete(uid, replacementName);
            await fetchUsers();
        } catch (err) {
            console.error('Error deleting external user:', err);
            const message = err instanceof Error ? err.message : 'Kunde inte ta bort användare';
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [fetchUsers]);

    const clearError = useCallback(() => setError(null), []);

    return {
        users,
        loading,
        error,
        submitting,
        refetch: fetchUsers,
        createUser,
        updateUser,
        deleteUser,
        clearError,
    };
};
